import React, { useEffect, useState } from 'react';
import { Printer } from 'lucide-react';
import { getCOA, getBukuBesar } from '../../api';

export default function BukuBesar() {
  const [data, setData] = useState([]);
  const [coaList, setCoaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAkun, setSelectedAkun] = useState('');

  useEffect(() => {
    getCOA().then(r => setCoaList(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (selectedAkun) params.akun_id = selectedAkun;
    getBukuBesar(params)
      .then(res => setData(res.data || []))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [selectedAkun]);

  const rupiah = (val) => {
    const n = Number(val);
    return isNaN(n) ? 'Rp 0' : `Rp ${n.toLocaleString('id-ID')}`;
  };

  // ===== PDF Export =====
  const generatePDF = () => {
    if (!data.length) return;
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentW = pageW - 2 * margin;

    const primary = [17, 205, 239];
    const primaryDark = [0, 150, 180];
    const dark = [23, 43, 77];
    const gray = [136, 152, 170];
    const lightGray = [248, 249, 252];
    const white = [255, 255, 255];

    const selectedName = selectedAkun
      ? coaList.find(c => c.id === Number(selectedAkun))
      : null;
    const akunLabel = selectedName
      ? `${selectedName.kode_akun} - ${selectedName.nama_akun}`
      : 'Semua Akun';

    let totalDebitAll = 0, totalKreditAll = 0;
    data.forEach(bb => {
      (bb.items || []).forEach(item => {
        totalDebitAll += item.debit || 0;
        totalKreditAll += item.kredit || 0;
      });
    });

    // ===== COVER / HEADER =====
    doc.setFillColor(...primary);
    doc.rect(0, 0, pageW, 6, 'F');
    doc.setFillColor(...primaryDark);
    doc.rect(0, 6, pageW, 2, 'F');

    // Company logo box
    doc.setFillColor(...dark);
    doc.roundedRect(margin, 16, 50, 30, 4, 4, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('L Synexa', margin + 25, 30, { align: 'center' });
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Sistem Informasi Armada', margin + 25, 37, { align: 'center' });

    // Title
    doc.setTextColor(...dark);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('BUKU BESAR', margin + 58, 25);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    doc.text('General Ledger Report', margin + 58, 33);
    doc.text(`Akun: ${akunLabel}`, margin + 58, 40);

    // Decorative line
    doc.setDrawColor(...primary);
    doc.setLineWidth(1.5);
    doc.line(margin, 52, pageW - margin, 52);
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(margin, 54, pageW - margin, 54);

    // Info box
    let y = 60;
    doc.setFillColor(...lightGray);
    doc.roundedRect(margin, y, contentW, 18, 3, 3, 'F');
    doc.setDrawColor(230, 230, 230);
    doc.roundedRect(margin, y, contentW, 18, 3, 3, 'S');

    doc.setTextColor(...dark);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('INFORMASI LAPORAN', margin + 8, y + 7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    doc.setFontSize(7);
    const now = new Date();
    doc.text(`Dicetak: ${now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`, margin + 8, y + 13);
    doc.text(`Total Akun: ${data.length} | Total Transaksi: ${data.reduce((s, bb) => s + (bb.items?.length || 0), 0)}`, margin + contentW / 2, y + 7);
    doc.text(`Total Debit: Rp ${totalDebitAll.toLocaleString('id-ID')} | Total Kredit: Rp ${totalKreditAll.toLocaleString('id-ID')}`, margin + contentW / 2, y + 13);

    y += 26;

    // ===== PER ACCOUNT TABLES =====
    data.forEach((bb) => {
      // Check if enough space for header + at least 3 rows
      if (y > pageH - 50) {
        doc.addPage();
        y = margin + 10;
      }

      // Account header bar
      doc.setFillColor(...dark);
      doc.roundedRect(margin, y, contentW, 10, 2, 2, 'F');
      doc.setTextColor(...white);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`${bb.akun?.kode_akun || ''} - ${bb.akun?.nama_akun || ''}`, margin + 6, y + 7);
      doc.text(`Saldo Akhir: Rp ${(bb.saldo_akhir || 0).toLocaleString('id-ID')}`, pageW - margin - 6, y + 7, { align: 'right' });
      y += 12;

      if (!bb.items || bb.items.length === 0) {
        doc.setTextColor(...gray);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.text('Tidak ada transaksi', margin + 6, y + 5);
        y += 12;
        return;
      }

      // Table
      const headers = [['Tanggal', 'No Jurnal', 'Keterangan', 'Debit (Rp)', 'Kredit (Rp)', 'Saldo (Rp)']];
      const rows = bb.items.map(item => [
        item.tanggal || '-',
        item.no_jurnal || '-',
        item.keterangan || '-',
        item.debit > 0 ? item.debit.toLocaleString('id-ID') : '',
        item.kredit > 0 ? item.kredit.toLocaleString('id-ID') : '',
        (item.saldo || 0).toLocaleString('id-ID'),
      ]);

      // Add saldo akhir row
      rows.push(['', '', 'SALDO AKHIR', '', '', (bb.saldo_akhir || 0).toLocaleString('id-ID')]);

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: headers,
        body: rows,
        styles: { fontSize: 7, cellPadding: 2.5, lineColor: [220, 220, 220], lineWidth: 0.5, textColor: [50, 50, 50] },
        headStyles: { fillColor: [...dark], textColor: [...white], fontStyle: 'bold', fontSize: 7, halign: 'center' },
        bodyStyles: { fontSize: 7 },
        alternateRowStyles: { fillColor: [245, 248, 252] },
        columnStyles: {
          0: { cellWidth: 22, halign: 'center' },
          1: { cellWidth: 30 },
          2: { cellWidth: 80 },
          3: { halign: 'right', fontStyle: 'bold', textColor: [5, 150, 105] },
          4: { halign: 'right', fontStyle: 'bold', textColor: [220, 38, 38] },
          5: { halign: 'right', fontStyle: 'bold', textColor: [...dark] },
        },
        didParseCell: function(tableData) {
          if (tableData.row.index === rows.length - 1) {
            tableData.cell.styles.fillColor = [240, 253, 244];
            tableData.cell.styles.textColor = [...dark];
            tableData.cell.styles.fontStyle = 'bold';
            tableData.cell.styles.lineWidth = 0.8;
            tableData.cell.styles.lineColor = [16, 185, 129];
          }
        },
      });

      y = doc.lastAutoTable.finalY + 10;
    });

    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setDrawColor(...primary);
      doc.setLineWidth(1);
      doc.line(margin, pageH - 15, pageW - margin, pageH - 15);
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.3);
      doc.line(margin, pageH - 14, pageW - margin, pageH - 14);

      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text('Layanan Synexa - Sistem Informasi Armada', margin, pageH - 9);
      doc.text(`Dicetak: ${now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, margin, pageH - 5);
      doc.setFont('helvetica', 'normal');
      doc.text(`Halaman ${i} dari ${pageCount}`, pageW - margin, pageH - 5, { align: 'right' });
    }

    doc.save('Laporan_Buku_Besar.pdf');
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Buku Besar</h2>
        <p className="page-desc">Ringkasan transaksi per akun — saldo debit, kredit, dan saldo akhir</p>
      </div>

      <div className="page-header-row" style={{ marginBottom: 16 }}>
        <div className="page-header-actions">
          {data.length > 0 && (
            <button className="btn btn-secondary" onClick={generatePDF}>
              <Printer size={16} /><span>Export PDF</span>
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>Filter Akun:</span>
          <select value={selectedAkun} onChange={e => setSelectedAkun(e.target.value)} style={{ minWidth: 250 }}>
            <option value="">-- Semua Akun --</option>
            {coaList.map(c => (
              <option key={c.id} value={c.id}>{c.kode_akun} - {c.nama_akun}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? <div className="page-loading">Memuat buku besar...</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {data.map(bb => (
            <div key={bb.akun?.id} className="table-wrapper">
              <h3 style={{ padding: '12px 16px', fontSize: '0.9rem', background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                {bb.akun?.kode_akun} - {bb.akun?.nama_akun}
                <span style={{ float: 'right', fontWeight: 700, color: 'var(--primary)' }}>
                  Saldo Akhir: {rupiah(bb.saldo_akhir)}
                </span>
              </h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Tanggal</th>
                    <th>No Jurnal</th>
                    <th>Keterangan</th>
                    <th style={{ textAlign: 'right' }}>Debit</th>
                    <th style={{ textAlign: 'right' }}>Kredit</th>
                    <th style={{ textAlign: 'right' }}>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {bb.items?.map((item, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: '0.8rem' }}>{item.tanggal}</td>
                      <td style={{ fontSize: '0.8rem' }}>{item.no_jurnal}</td>
                      <td style={{ fontSize: '0.82rem' }}>{item.keterangan}</td>
                      <td style={{ textAlign: 'right', fontSize: '0.82rem', color: item.debit > 0 ? '#065f46' : 'inherit' }}>
                        {item.debit > 0 ? rupiah(item.debit) : '-'}
                      </td>
                      <td style={{ textAlign: 'right', fontSize: '0.82rem', color: item.kredit > 0 ? '#92400e' : 'inherit' }}>
                        {item.kredit > 0 ? rupiah(item.kredit) : '-'}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, fontSize: '0.85rem' }}>{rupiah(item.saldo)}</td>
                    </tr>
                  ))}
                  {(!bb.items || bb.items.length === 0) && (
                    <tr><td colSpan={6} className="text-center" style={{ padding: 20, color: 'var(--gray-400)' }}>Tidak ada transaksi</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ))}
          {data.length === 0 && (
            <div className="page-loading">Belum ada data buku besar</div>
          )}
        </div>
      )}
    </div>
  );
}
